"use client";

import React, { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, Layers, Gauge, Sparkles, ShieldCheck, SlidersVertical, FileCheck } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
interface TechFeature {
  title: string;
  description: string;
  badge: string;
  specs: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  linkTo: string;
  linkText: string;
}
const processesData = [{
  id: "raw-material",
  category: "Raw Material ID",
  title: "Raw Material Verification & Ingestion Testing",
  description: "Rapid handheld and benchtop spectroscopic verification for active ingredients, botanical extracts, vitamins, and cosmetic excipients.",
  icon: FlaskConical,
  items: [{
    title: "Handheld Raman & NIR Identification",
    description: "Verify drums, bags, and liquid containers directly at receiving docks without opening packaging, preventing contamination.",
    badge: "Receiving QC",
    specs: "Rapid non-destructive scan • Secure data integrity • Pre-built botanical libraries",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_spectroscopy.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_spectroscopy.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/raw_material_spectroscopy.png-wvc-sizes",
    linkTo: "/RawMaterialIdentificationPage",
    linkText: "Explore Raw Material ID"
  }, {
    title: "Microbiology & Purity Screening",
    description: "Assay verification and impurity detection for sensitive natural cosmetic bases and dietary botanical supplements.",
    badge: "Purity Check",
    specs: "High sensitivity • Rapid screening workflow • Automated reporting",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cosmetic_purity_testing.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cosmetic_purity_testing.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/cosmetic_purity_testing.png-wvc-sizes",
    linkTo: "/SolutionsPage",
    linkText: "View Purity Solutions"
  }]
}, {
  id: "blend-drying",
  category: "Blending & Drying",
  title: "In-line Process Monitoring & Drying Endpoints",
  description: "Real-time process analytical technology (PAT) probes to track homogeneity in powders, creams, and fluid bed dryers without manual grab sampling.",
  icon: Layers,
  items: [{
    title: "Non-Invasive Blend Uniformity Monitoring",
    description: "Wireless near-infrared sensors mounted directly onto V-blenders and bin tumblers to determine true blend completion in real time.",
    badge: "In-line PAT",
    specs: "Wireless telemetry • ATEX certified • Real-time RSD calculation",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_sensor.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_sensor.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/blend_uniformity_sensor.png-wvc-sizes",
    linkTo: "/BlendUniformityPage",
    linkText: "Discover Blend Uniformity"
  }, {
    title: "Fluid Bed & Oven Drying Endpoint Detection",
    description: "Continuous moisture and solvent monitoring inside drying chambers to prevent over-drying, protect active potencies, and cut cycle times.",
    badge: "Drying Endpoint",
    specs: "Direct insertion probes • High moisture sensitivity • Dynamic endpoint alarms",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_analyzer.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_analyzer.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/drying_endpoint_analyzer.png-wvc-sizes",
    linkTo: "/DryingEndpointPage",
    linkText: "Learn About Drying Endpoints"
  }]
}, {
  id: "tablet-powder",
  category: "Finished Dose Testing",
  title: "Physical Testing for Tablets, Powders & Emulsions",
  description: "Automated physical measurement systems guaranteeing batch uniformity, dissolution rates, and structural integrity for oral doses and topical products.",
  icon: Gauge,
  items: [{
    title: "Automated Tablet & Capsule Physical Testing",
    description: "Multi-parameter testing stations evaluating weight, thickness, diameter, hardness, and disintegration times for nutraceutical solids.",
    badge: "Physical QC",
    specs: "5-parameter auto testing • Direct balance links • Touchscreen controls",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_station.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_station.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/tablet_testing_station.png-wvc-sizes",
    linkTo: "/TabletTestingPage",
    linkText: "View Tablet Testers"
  }, {
    title: "Powder Flowability & Bulk Characterisation",
    description: "Assess tapped density, shear cell dynamics, and compressibility to prevent hopper bridging and tablet press weight variation.",
    badge: "Powder Dynamics",
    specs: "USP/EP compliant methods • Automated tap counters • Flow index metrics",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/powder_testing_lab.png-wvc-sizes",
    linkTo: "/PowderTestingPage",
    linkText: "Explore Powder Testing"
  }]
}, {
  id: "solvent-safety",
  category: "Solvent & Safety",
  title: "Eco-Friendly Solvent Recovery & Environmental QC",
  description: "Closed-loop analytical solvent purification and plant safety technologies designed for active extraction, synthesis, and cleanroom monitoring.",
  icon: Sparkles,
  items: [{
    title: "HPLC Solvent Recovery (ReSolve)",
    description: "Recycle up to 90% of chromatographic isocratic mobile phases in analytical and purification workflows without baseline disturbance.",
    badge: "Sustainability",
    specs: "Up to 90% solvent reuse • Zero baseline shift • Auto valve switching",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/solvent_recovery_unit.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/solvent_recovery_unit.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/solvent_recovery_unit.png-wvc-sizes",
    linkTo: "/HplcSolventRecoveryResolvePage",
    linkText: "See Solvent Recovery"
  }, {
    title: "Facility Environmental & Safety Monitoring",
    description: "Continuous volatile organic compound (VOC) leak detection and cleanroom air monitoring systems for manufacturing integrity.",
    badge: "Facility Safety",
    specs: "Multi-point sampling • Immediate alert thresholds • Data logging",
    imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/facility_safety_sensor.png",
    imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/facility_safety_sensor.png-wvc-srcset",
    imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/facility_safety_sensor.png-wvc-sizes",
    linkTo: "/SafetyAndSecurityPage",
    linkText: "Check Safety Systems"
  }]
}];
const highlightsList = [{
  title: "Regulatory Compliance Support",
  description: "Instrumentation and data systems configured to support industry quality and traceability requirements.",
  icon: ShieldCheck
}, {
  title: "Integrated Validation & IQ/OQ",
  description: "Every deployment includes full protocol execution, qualification documentation, and user operator training.",
  icon: FileCheck
}, {
  title: "Custom Process Adapters",
  description: "Non-invasive optical windows and sanitary triclamp fittings tailored for reactors, blenders, and conveyors.",
  icon: SlidersVertical
}];
export default function ProcessOverview() {
  const [activeTab, setActiveTab] = useState("raw-material");
  const prefersReducedMotion = useReducedMotion();
  const currentCategory = processesData.find(p => p.id === activeTab) || processesData[0];
  return <section id="technologyprocessoverview" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="page-container flex flex-col gap-12">
        {/* Section Header */}
        <div className="flex flex-col items-start gap-4 max-w-3xl">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Process Analytical Technology
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Technology & Process Solutions for Regulated Manufacturing
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
            Engineered instrumentation and on-line monitoring systems purpose-built for nutraceutical, dietary supplement, and cosmetic production across Ireland and the UK.
          </p>
        </div>

        {/* Category Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <div className="overflow-x-auto pb-2">
            <TabsList className="h-auto p-1.5 bg-muted/60 border border-border inline-flex rounded-xl gap-1.5 w-full md:w-auto">
              {processesData.map((item, index) => {
              const IconComponent = item.icon;
              return <div key={item.id} data-index={index}>
                    <TabsTrigger value={item.id} className="px-4 py-2.5 rounded-lg text-sm font-medium transition-all gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm">
                      <IconComponent className="size-4 shrink-0 text-primary" />
                      <span>{item.category}</span>
                    </TabsTrigger>
                  </div>;
            })}
            </TabsList>
          </div>

          {/* Active Tab Overview Banner */}
          <div className="mt-8 rounded-xl bg-secondary/40 border border-border p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="space-y-2 max-w-2xl">
              <span className="text-xs font-semibold tracking-wider text-primary uppercase">Current Focus</span>
              <h3 className="text-2xl font-bold text-foreground">{currentCategory.title}</h3>
              <p className="text-sm md:text-base text-muted-foreground">{currentCategory.description}</p>
            </div>
            <Button asChild variant="default" size="default" className="shrink-0">
              <Link to="/ProcessAnalyticalTechnologyPage">
                Learn About PAT
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>

          {/* Tab Content Cards Grid */}
          <AnimatePresence mode="wait">
            <motion.div key={activeTab} initial={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={prefersReducedMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: -12
          }} transition={{
            duration: 0.25
          }} className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
              {currentCategory.items.map((feature: TechFeature, idx: number) => <div key={feature.title} data-index={idx} className="h-full">
                  <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow flex flex-col justify-between overflow-hidden">
                    <div className="relative h-56 w-full overflow-hidden bg-muted">
                      <img src={feature.imageSrc} data-wvc-srcset={feature.imageSrcset} data-wvc-sizes={feature.imageSizes} alt={feature.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />
                      <div className="absolute top-3 left-3">
                        <Badge variant="default" className="shadow-xs font-medium">
                          {feature.badge}
                        </Badge>
                      </div>
                    </div>

                    <div>
                      <CardHeader className="space-y-2">
                        <CardTitle className="text-xl font-bold text-card-foreground">
                          {feature.title}
                        </CardTitle>
                        <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                          {feature.description}
                        </CardDescription>
                      </CardHeader>

                      <CardContent>
                        <div className="rounded-lg bg-muted/50 p-3.5 border border-border/60">
                          <div className="text-xs font-semibold text-foreground/80 mb-1 flex items-center gap-1.5">
                            <CheckCircle2 className="size-3.5 text-primary" />
                            <span>Key Capabilities & Specifications</span>
                          </div>
                          <p className="text-xs text-muted-foreground font-mono">
                            {feature.specs}
                          </p>
                        </div>
                      </CardContent>
                    </div>

                    <div className="p-6 pt-0 mt-auto">
                      <Separator className="mb-4" />
                      <div className="flex items-center justify-between">
                        <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                          <Link to={feature.linkTo}>
                            {feature.linkText}
                            <ArrowRight className="size-3.5 ml-1" />
                          </Link>
                        </Button>
                        <span className="hidden sm:inline text-xs text-muted-foreground font-medium">
                          IQ/OQ Supported
                        </span>
                      </div>
                    </div>
                  </Card>
                </div>)}
            </motion.div>
          </AnimatePresence>
        </Tabs>

        {/* Feature Highlights Footer Bar */}
        <div className="mt-6 pt-10 border-t border-border">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {highlightsList.map((highlight, index) => {
            const IconComp = highlight.icon;
            return <div key={highlight.title} data-index={index} className="flex items-start gap-4 p-4 rounded-xl bg-card border border-border">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                    <IconComp className="size-5" />
                  </div>
                  <div className="space-y-1">
                    <h4 className="text-sm font-semibold text-foreground">{highlight.title}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {highlight.description}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {/* Action Bottom Banner */}
        <div className="rounded-2xl bg-muted/80 border border-border p-8 text-center flex flex-col items-center justify-center gap-4">
          <h3 className="text-xl md:text-2xl font-bold text-foreground">
            Looking for a specific analytical method or qualification support?
          </h3>
          <p className="text-sm md:text-base text-muted-foreground max-w-xl">
            Our technical specialists evaluate your nutraceutical and cosmetic process streams to configure the right testing instrument.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 mt-2">
            <Button asChild variant="default" size="lg">
              <Link to="/ContactPage">
                Request Application Consultation
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/ProductsPage">
                Browse Complete Catalog
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}